import { Check, Loader2 } from "lucide-react";
import { RadioGroup as RadioGroupPrimitive } from "radix-ui";
import { type KeyboardEvent, useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { NativeSelect } from "@/components/NativeSelect";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";

/**
 * The typed controls of a settings row (openspec change settings-typed-controls, D1, D2, D9).
 * Each is named by its row's label (`labelledBy`) and described by the row's provenance line.
 * `busy` (a save running) keeps the control focusable but ignores changes, so focus never drops
 * to the page while "Saving…" shows; `disabled` is for rows that cannot change at all.
 */
interface ControlProps {
  labelledBy: string;
  describedBy?: string;
  disabled?: boolean;
  busy?: boolean;
}

const described = (...ids: (string | undefined)[]) => ids.filter(Boolean).join(" ") || undefined;

/** On/off, saved on change; the word beside it says the state. */
export function SettingSwitch(
  props: ControlProps & { checked: boolean; onChange: (on: boolean) => void },
) {
  const { t } = useTranslation();
  return (
    <>
      <span
        aria-hidden="true"
        className={cn("text-sm", props.checked ? "font-medium text-fg" : "text-fg-secondary")}
      >
        {props.checked ? t("settingsControls.on") : t("settingsControls.off")}
      </span>
      <Switch
        checked={props.checked}
        disabled={props.disabled}
        aria-disabled={props.busy || undefined}
        aria-labelledby={props.labelledBy}
        aria-describedby={props.describedBy}
        onCheckedChange={(on) => {
          if (!props.busy) {
            props.onChange(on);
          }
        }}
      />
    </>
  );
}

export interface SegmentOption {
  value: string;
  label: string;
  disabled?: boolean;
}

/**
 * Two or three short options, one chosen: radio semantics (arrow keys move and choose), drawn
 * as one sunken bar with the chosen option raised. Disabled options stay visible.
 */
export function SettingSegmented(
  props: ControlProps & {
    value: string;
    options: readonly SegmentOption[];
    onChange: (value: string) => void;
  },
) {
  return (
    <RadioGroupPrimitive.Root
      value={props.value}
      onValueChange={(next) => {
        if (!props.busy && next !== props.value) {
          props.onChange(next);
        }
      }}
      aria-labelledby={props.labelledBy}
      aria-describedby={props.describedBy}
      aria-disabled={props.busy || undefined}
      disabled={props.disabled}
      orientation="horizontal"
      className="inline-flex h-8 items-center gap-0.5 rounded-md border border-line bg-surface-sunken p-0.5"
    >
      {props.options.map((o) => (
        <RadioGroupPrimitive.Item
          key={o.value}
          value={o.value}
          disabled={o.disabled}
          className="h-full rounded-[5px] px-2.5 text-[13px] font-medium whitespace-nowrap text-fg-secondary outline-none transition-colors duration-(--duration-base) hover:text-fg focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:text-fg-muted disabled:line-through disabled:decoration-fg-muted/60 data-[state=checked]:bg-surface-raised data-[state=checked]:text-fg data-[state=checked]:shadow-xs data-[state=checked]:ring-1 data-[state=checked]:ring-line"
        >
          {o.label}
        </RadioGroupPrimitive.Item>
      ))}
    </RadioGroupPrimitive.Root>
  );
}

/**
 * Options with long labels or from data: a native select, saved on change (a native select
 * fires `change` on commit, not while arrowing through an open list). One option reads as text.
 */
export function SettingSelect(
  props: ControlProps & {
    value: string;
    options: readonly SegmentOption[];
    onChange: (value: string) => void;
  },
) {
  if (props.options.length === 1 && props.options[0]?.value === props.value) {
    return <span className="text-sm text-fg">{props.options[0].label}</span>;
  }
  return (
    <NativeSelect
      aria-labelledby={props.labelledBy}
      aria-describedby={props.describedBy}
      aria-disabled={props.busy || undefined}
      disabled={props.disabled}
      value={props.value}
      className="w-full @xl:w-72"
      onChange={(e) => {
        const next = e.currentTarget.value;
        if (!props.busy && next !== props.value) {
          props.onChange(next);
        }
      }}
    >
      {props.options.map((o) => (
        <option key={o.value} value={o.value} disabled={o.disabled}>
          {o.label}
        </option>
      ))}
    </NativeSelect>
  );
}

/** A small multi-choice as toggle chips (`aria-pressed`); at least one stays on. */
export function SettingChips(
  props: ControlProps & {
    value: readonly string[];
    options: readonly SegmentOption[];
    onChange: (value: string[]) => void;
  },
) {
  const picked = props.value;
  return (
    // biome-ignore lint/a11y/useSemanticElements: a group of toggle buttons, not form fields.
    <span
      role="group"
      aria-labelledby={props.labelledBy}
      aria-describedby={props.describedBy}
      className="inline-flex flex-wrap gap-1.5"
    >
      {props.options.map((o) => {
        const on = picked.includes(o.value);
        const last = on && picked.length === 1;
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={on}
            aria-disabled={props.busy || undefined}
            disabled={props.disabled || last || o.disabled}
            onClick={() => {
              if (props.busy) {
                return;
              }
              props.onChange(
                on
                  ? picked.filter((v) => v !== o.value)
                  : props.options
                      .map((x) => x.value)
                      .filter((v) => v === o.value || picked.includes(v)),
              );
            }}
            className={cn(
              "inline-flex h-7 items-center gap-1 rounded-full border px-2.5 text-[13px] font-medium outline-none transition-colors duration-(--duration-base) focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:cursor-not-allowed",
              on
                ? "border-primary bg-primary text-primary-foreground disabled:opacity-70"
                : "border-line bg-surface-raised text-fg-secondary hover:text-fg disabled:opacity-50",
            )}
          >
            {on ? <Check aria-hidden="true" className="size-3" strokeWidth={2} /> : null}
            {o.label}
          </button>
        );
      })}
    </span>
  );
}

export interface NumberRange {
  min: number | null;
  max: number | null;
}

/** The range sentence of a refused number, or null when `text` is a whole number in range. */
function numberProblem(text: string, range: NumberRange, t: (k: string, o?: object) => string) {
  const n = Number(text);
  const ok =
    text.trim() !== "" &&
    Number.isInteger(n) &&
    (range.min === null || n >= range.min) &&
    (range.max === null || n <= range.max);
  if (ok) {
    return null;
  }
  return range.max === null
    ? t("settingsControls.rangeFrom", { min: range.min ?? 0 })
    : t("settingsControls.range", { min: range.min ?? 0, max: range.max });
}

/**
 * A whole number with its unit inside the field, committed on Enter or leaving the field;
 * Escape restores the saved value; a number out of range shows the range and is not sent.
 */
export function SettingNumber(
  props: ControlProps & {
    value: number | null;
    unit: string;
    prefix?: boolean;
    range: NumberRange;
    onCommit: (value: number) => void;
    /** Focus the field when it appears (switched on with no value yet). */
    autoFocus?: boolean;
    /** Left without a value typed (a switch + number then goes back off). */
    onAbandon?: () => void;
    /** Whether a typed number waits for its commit (unsaved work). */
    onDraft?: (pending: boolean) => void;
    /** What an empty field means ("No limit"). */
    placeholder?: string;
  },
) {
  const { t } = useTranslation();
  const errId = useId();
  // The text typed since the last commit; null: the saved value shows.
  const [draft, setDraft] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const onDraft = props.onDraft;
  const drafting = draft !== null;
  useEffect(() => {
    onDraft?.(drafting);
    // A field removed mid-draft (switched off, row gone) no longer holds work.
    return drafting ? () => onDraft?.(false) : undefined;
  }, [onDraft, drafting]);
  const shown = draft ?? (props.value === null ? "" : String(props.value));
  const commit = () => {
    if (props.busy) {
      return;
    }
    if (draft === null || (draft.trim() === "" && props.value === null)) {
      if (props.value === null) {
        setDraft(null);
        setProblem(null);
        props.onAbandon?.();
      }
      return;
    }
    const issue = numberProblem(draft, props.range, t as never);
    if (issue) {
      setProblem(issue);
      return;
    }
    setDraft(null);
    setProblem(null);
    if (Number(draft) !== props.value) {
      props.onCommit(Number(draft));
    }
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commit();
    } else if (e.key === "Escape" && (draft !== null || problem)) {
      e.preventDefault();
      setDraft(null);
      setProblem(null);
    }
  };
  return (
    <span className="flex flex-col items-start gap-1 @xl:items-end">
      <span
        className={cn(
          "inline-flex h-8 items-center rounded-md border bg-surface-raised shadow-xs focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-1",
          problem ? "border-danger-border" : "border-input",
          props.disabled && "opacity-50",
        )}
      >
        {props.prefix ? (
          <span aria-hidden="true" className="pl-2.5 text-sm text-fg-muted tabular-nums">
            {props.unit}
          </span>
        ) : null}
        <input
          inputMode="numeric"
          // biome-ignore lint/a11y/noAutofocus: the field appears in answer to its switch.
          autoFocus={props.autoFocus}
          aria-labelledby={props.labelledBy}
          aria-describedby={described(problem ? errId : undefined, props.describedBy)}
          aria-invalid={problem ? true : undefined}
          aria-readonly={props.busy || undefined}
          readOnly={props.busy}
          disabled={props.disabled}
          placeholder={props.placeholder}
          value={shown}
          onChange={(e) => {
            setDraft(e.currentTarget.value);
            setProblem(null);
          }}
          onKeyDown={onKeyDown}
          onBlur={commit}
          className={cn(
            "h-full bg-transparent text-right text-sm text-fg tabular-nums outline-none placeholder:text-fg-muted",
            // Room for the placeholder's words while empty.
            props.placeholder && shown === "" ? "w-28" : "w-16",
            props.prefix ? "pr-2.5 pl-1" : "pl-2.5",
          )}
        />
        {props.prefix ? null : (
          <span aria-hidden="true" className="pr-2.5 pl-1.5 text-sm text-fg-muted">
            {/* An empty field reads its placeholder ("No limit") alone. */}
            {props.placeholder && shown === "" ? null : props.unit}
          </span>
        )}
      </span>
      {problem ? (
        <span id={errId} className="text-xs text-danger-fg">
          {problem}
        </span>
      ) : null}
    </span>
  );
}

/**
 * A number whose empty value means "off", "no limit" or "the operator's budget": a switch turns
 * it on (with `onValue`, or an empty field whose first value saves), the field holds the number.
 */
export function SettingSwitchNumber(
  props: ControlProps & {
    value: number | null;
    unit: string;
    range: NumberRange;
    /** The switch's own word ("Limit", "Own budget"). */
    switchLabel: string;
    /** What the row reads while off ("No limit", "Operator's budget"). */
    offText: string;
    onValue: number | null;
    onCommit: (value: number | null) => void;
    /**
     * A project's number without "off" (slice B): the switch is not offered and the field always
     * shows (empty while the project has no number of its own to narrow).
     */
    noOff?: boolean;
    onDraft?: (pending: boolean) => void;
  },
) {
  const switchId = useId();
  // Switched on with no value to start from: the field shows, empty, until its first commit.
  const [opening, setOpening] = useState(false);
  const on = props.value !== null || opening || props.noOff === true;
  return (
    <>
      {on ? (
        <SettingNumber
          labelledBy={props.labelledBy}
          describedBy={props.describedBy}
          disabled={props.disabled}
          busy={props.busy}
          value={props.value}
          unit={props.unit}
          range={props.range}
          autoFocus={opening}
          // A project's field with no number of its own reads what empty means.
          placeholder={props.noOff ? props.offText : undefined}
          onDraft={props.onDraft}
          onAbandon={() => setOpening(false)}
          onCommit={(n) => {
            setOpening(false);
            props.onCommit(n);
          }}
        />
      ) : (
        <span className="text-sm text-fg-secondary">{props.offText}</span>
      )}
      {props.noOff ? null : (
        <>
          <span id={switchId} className="sr-only">
            {props.switchLabel}
          </span>
          <Switch
            checked={on}
            disabled={props.disabled}
            aria-disabled={props.busy || undefined}
            aria-labelledby={`${props.labelledBy} ${switchId}`}
            aria-describedby={props.describedBy}
            onCheckedChange={(next) => {
              if (props.busy) {
                return;
              }
              if (!next) {
                setOpening(false);
                if (props.value !== null) {
                  props.onCommit(null);
                }
              } else if (props.onValue !== null) {
                props.onCommit(props.onValue);
              } else {
                setOpening(true);
              }
            }}
          />
        </>
      )}
    </>
  );
}

/**
 * A short text, committed on Enter or leaving the field; Escape restores the saved text; an
 * empty field saves null (the default), a text over `max` is refused in place.
 */
export function SettingText(
  props: ControlProps & {
    value: string | null;
    placeholder?: string;
    max: number;
    invalidText: string;
    onCommit: (value: string | null) => void;
    /** Each keystroke (to forget a refusal). */
    onEdit?: () => void;
  },
) {
  const errId = useId();
  const [draft, setDraft] = useState<string | null>(null);
  const [problem, setProblem] = useState(false);
  const shown = draft ?? props.value ?? "";
  const commit = () => {
    if (draft === null || props.busy) {
      return;
    }
    const trimmed = draft.trim();
    if (trimmed.length > props.max) {
      setProblem(true);
      return;
    }
    setDraft(null);
    setProblem(false);
    const next = trimmed === "" ? null : trimmed;
    if (next !== props.value) {
      props.onCommit(next);
    }
  };
  return (
    <span className="flex w-full flex-col items-start gap-1 @xl:w-72 @xl:items-end">
      <input
        type="text"
        autoComplete="off"
        aria-labelledby={props.labelledBy}
        aria-describedby={described(problem ? errId : undefined, props.describedBy)}
        aria-invalid={problem || undefined}
        aria-readonly={props.busy || undefined}
        readOnly={props.busy}
        disabled={props.disabled}
        placeholder={props.placeholder}
        value={shown}
        maxLength={props.max + 20}
        onChange={(e) => {
          setDraft(e.currentTarget.value);
          setProblem(false);
          props.onEdit?.();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
          } else if (e.key === "Escape" && draft !== null) {
            e.preventDefault();
            setDraft(null);
            setProblem(false);
          }
        }}
        onBlur={commit}
        className={cn(
          "h-8 w-full min-w-0 rounded-md border bg-surface-raised px-2.5 text-sm text-fg shadow-xs outline-none placeholder:text-fg-muted focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:opacity-50",
          problem ? "border-danger-border" : "border-input",
        )}
      />
      {problem ? (
        <span id={errId} className="text-xs text-danger-fg">
          {props.invalidText}
        </span>
      ) : null}
    </span>
  );
}

/** Where a save stands, beside the control: a polite live region. */
export function SaveState(props: { state: "idle" | "saving" | "saved"; className?: string }) {
  const { t } = useTranslation();
  return (
    <span
      role="status"
      className={cn(
        "inline-flex h-5 min-w-0 items-center gap-1 text-xs whitespace-nowrap text-fg-muted",
        props.className,
      )}
    >
      {props.state === "saving" ? (
        <>
          <Loader2 aria-hidden="true" className="size-3 animate-spin" />
          {t("settingsControls.saving")}
        </>
      ) : props.state === "saved" ? (
        <>
          <Check aria-hidden="true" className="size-3 text-success-fg" />
          {t("settingsControls.saved")}
        </>
      ) : null}
    </span>
  );
}
